<template>
	<section v-loading="pageLoading" class="provider-benefit-config-page" element-loading-text="正在加载商品权益信息...">
		<header class="config-page-heading">
			<div>
				<h2>{{ isEditMode ? '编辑商品权益配置' : '新增商品权益配置' }}</h2>
				<p>配置虚拟商品包含的功能权益，商品下单后按当前清单下发订单权益</p>
			</div>
			<el-button icon="el-icon-back" :disabled="pageLoading || saving" @click="handleBack">返回配置管理</el-button>
		</header>

		<div class="config-guide-card">
			<span class="guide-icon"><i class="el-icon-info"></i></span>
			<div>
				<strong>配置说明</strong>
				<p>次数型权益按使用次数扣减，时间型权益按有效天数计算。请确认权益清单和额度后再保存。</p>
			</div>
		</div>

		<el-form ref="configForm" :model="formData" :rules="configRules" label-position="top" class="config-main-form">
			<div class="config-form-card">
				<div class="config-section-heading">
					<span class="section-icon"><i class="el-icon-goods"></i></span>
					<div>
						<strong>商品信息</strong>
						<small>选择需要配置权益的已发布商品</small>
					</div>
				</div>
				<el-row :gutter="24">
					<el-col :span="12">
						<el-form-item label="商品名称" prop="productId">
							<el-select
								v-model="formData.productId"
								filterable
								placeholder="请选择已上架商品"
								popper-class="provider-product-select-dropdown"
								class="full-control"
								:disabled="pageLoading || saving"
							>
								<el-option
									v-for="item in publishedProducts"
									:key="item.id"
									:label="item.goodsName"
									:value="item.id"
									:disabled="isProductConfigured(item.id)"
								>
									<div class="product-option-main">{{ item.goodsName }}</div>
									<div class="product-option-code">编号：{{ item.goodsCode }}</div>
								</el-option>
							</el-select>
						</el-form-item>
					</el-col>
					<el-col :span="6">
						<el-form-item label="售价">
							<el-input :value="priceText" disabled><template slot="append">元/件</template></el-input>
						</el-form-item>
					</el-col>
					<el-col :span="6">
						<el-form-item label="已配置功能权益">
							<el-input :value="formData.benefits.length" disabled><template slot="append">项</template></el-input>
						</el-form-item>
					</el-col>
				</el-row>
			</div>
		</el-form>

		<div class="config-form-card benefit-list-card">
			<div class="config-section-heading has-action">
				<span class="section-icon is-green"><i class="el-icon-connection"></i></span>
				<div>
					<strong>权益信息</strong>
					<small>当前商品共包含 {{ formData.benefits.length }} 项功能权益</small>
				</div>
				<el-button type="primary" plain icon="el-icon-plus" :disabled="pageLoading || saving" @click="handleAddBenefit">
					新增权益
				</el-button>
			</div>

			<el-table :data="formData.benefits" border empty-text="暂未配置功能权益，请点击“新增权益”添加">
				<el-table-column label="权益名称" min-width="190">
					<template slot-scope="{ row }">
						<strong class="benefit-name">{{ row.name }}</strong>
					</template>
				</el-table-column>
				<el-table-column label="计量类型" width="120" align="center">
					<template slot-scope="{ row }">
						<span :class="['measure-tag', row.measureType === 'TIME' ? 'is-time' : 'is-count']">
							{{ getMeasureTypeText(row.measureType) }}
						</span>
					</template>
				</el-table-column>
				<el-table-column prop="description" label="功能说明" min-width="330" show-overflow-tooltip></el-table-column>
				<el-table-column label="计量额度" width="130" align="center">
					<template slot-scope="{ row }">
						<strong class="quota-text">{{ row.quota }}</strong>
						{{ row.unit }}
					</template>
				</el-table-column>
				<el-table-column label="操作" width="170" align="center">
					<template slot-scope="scope">
						<el-button size="small" plain icon="el-icon-edit-outline" @click="handleEditBenefit(scope.row, scope.$index)">
							编辑
						</el-button>
						<el-button
							size="small"
							type="danger"
							plain
							icon="el-icon-delete"
							@click="handleDeleteBenefit(scope.row, scope.$index)"
						>
							删除
						</el-button>
					</template>
				</el-table-column>
			</el-table>
		</div>

		<div class="page-actions">
			<el-button :disabled="pageLoading || saving" @click="handleBack">取消</el-button>
			<el-button type="primary" icon="el-icon-check" :loading="saving" :disabled="pageLoading" @click="handleSave">
				保存配置
			</el-button>
		</div>

		<jr-dialog
			:visible.sync="benefitDialogVisible"
			:title="editingBenefitIndex > -1 ? '编辑功能权益' : '新增功能权益'"
			width="720px"
			:show-btn="false"
			custom-class="provider-benefit-dialog"
		>
			<template slot="content">
				<el-form ref="benefitForm" :model="benefitForm" :rules="benefitRules" label-position="top" class="benefit-dialog-form">
					<el-form-item label="功能权益" prop="benefitId">
						<el-select
							v-model="benefitForm.benefitId"
							filterable
							placeholder="请选择已维护的功能权益"
							class="full-control"
							@change="handleBenefitChange"
						>
							<el-option
								v-for="item in benefitCatalog"
								:key="item.id"
								:label="item.name"
								:value="item.id"
								:disabled="isBenefitConfigured(item.id)"
							></el-option>
						</el-select>
					</el-form-item>
					<el-form-item label="功能说明">
						<el-input :value="benefitForm.description" type="textarea" :rows="3" resize="none" disabled></el-input>
					</el-form-item>
					<el-row :gutter="20">
						<el-col :span="10">
							<el-form-item label="计量类型">
								<el-input :value="measureTypeDetail" disabled></el-input>
							</el-form-item>
						</el-col>
						<el-col :span="9">
							<el-form-item label="额度" prop="quota">
								<el-input-number
									v-model="benefitForm.quota"
									:min="1"
									:max="999999"
									:precision="0"
									controls-position="right"
									class="full-control"
								></el-input-number>
							</el-form-item>
						</el-col>
						<el-col :span="5">
							<el-form-item label="单位"><el-input :value="benefitForm.unit || '-'" disabled></el-input></el-form-item>
						</el-col>
					</el-row>
				</el-form>
				<div class="dialog-actions">
					<el-button @click="benefitDialogVisible = false">取消</el-button>
					<el-button type="primary" @click="handleConfirmBenefit">确定</el-button>
				</div>
			</template>
		</jr-dialog>
	</section>
</template>

<script>
	import JrDialog from '@/components/Dialog'
	import {
		queryEnabledGoodsBenefits,
		queryMyGoodsBenefits,
		queryMyPublishedGoodsBenefitPage,
		saveMyGoodsBenefitConfig
	} from '@/api/mall'

	const createBenefitForm = () => ({
		benefitId: '',
		name: '',
		description: '',
		measureType: '',
		quota: 1,
		unit: ''
	})

	export default {
		name: 'ProviderConfigurationEdit',
		components: { JrDialog },
		data() {
			return {
				pageLoading: false,
				saving: false,
				productOptions: [],
				benefitCatalog: [],
				configuredProductIds: [],
				// 编辑时记录原商品 ID，切换商品后仍允许用户重新选择原商品。
				originalProductId: '',
				formData: {
					id: '',
					productId: '',
					benefits: []
				},
				configRules: {
					productId: [{ required: true, message: '请选择需要配置权益的商品', trigger: 'change' }]
				},
				benefitDialogVisible: false,
				editingBenefitIndex: -1,
				benefitForm: createBenefitForm(),
				benefitRules: {
					benefitId: [{ required: true, message: '请选择功能权益', trigger: 'change' }],
					quota: [{ required: true, message: '请输入计量额度', trigger: 'change' }]
				}
			}
		},
		computed: {
			// 是否从配置列表进入编辑状态，新增状态下不会携带配置 ID。
			isEditMode() {
				return Boolean(this.$route.query.id)
			},
			// 商品下拉只展示当前用户已上架的商品。
			publishedProducts() {
				return this.productOptions.filter(item => Number(item.rowState) === 8)
			},
			selectedProduct() {
				return this.productOptions.find(item => item.id === this.formData.productId) || null
			},
			// 售价由所选商品自动带入，不作为单独的配置字段保存。
			priceText() {
				return this.selectedProduct ? Number(this.selectedProduct.price).toFixed(2) : '-'
			},
			measureTypeDetail() {
				if (this.benefitForm.measureType === 'COUNT') return '次数型（额度/次）'
				if (this.benefitForm.measureType === 'TIME') return '时间型（有效期/天）'
				return '-'
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			// 加载当前用户已上架商品、已启用权益模板及商品权益快照。
			async initPage() {
				this.pageLoading = true
				try {
					const [goodsResponse, benefitResponse] = await Promise.all([
						queryMyPublishedGoodsBenefitPage({ page: 1, size: 1000 }),
						queryEnabledGoodsBenefits({})
					])
					const goodsData = goodsResponse.data || {}
					const goodsRecords = goodsData.records || goodsData.list || []
					this.productOptions = goodsRecords.map(this.mapProduct)
					this.benefitCatalog = (benefitResponse.data || []).map(this.mapBenefitTemplate)
					this.configuredProductIds = this.productOptions.filter(item => item.benefits.length > 0).map(item => item.id)
					if (!this.isEditMode) return

					const goodsId = this.$route.query.id
					const product = this.productOptions.find(item => item.id === goodsId)
					if (!product) {
						this.$message.warning('未找到对应的商品权益配置')
						this.handleBack()
						return
					}
					const response = await queryMyGoodsBenefits({ goodsId })
					this.originalProductId = goodsId
					this.formData = {
						id: goodsId,
						productId: goodsId,
						benefits: (response.data || []).map(this.mapLinkedBenefit)
					}
				} finally {
					this.pageLoading = false
				}
			},
			mapProduct(goods) {
				return {
					id: goods.id,
					goodsName: goods.goodsTitle || '-',
					goodsCode: goods.orderCode || goods.id,
					price: goods.sellingPrice,
					rowState: goods.rowState,
					benefits: goods.goodsBenefits || []
				}
			},
			normalizeMeasureType(measureType) {
				return measureType === 'PERIOD' || measureType === 'TIME' ? 'TIME' : 'COUNT'
			},
			mapBenefitTemplate(item) {
				const measureType = this.normalizeMeasureType(item.defaultMeasureType)
				return {
					id: item.id,
					name: item.memberBenefitName,
					description: item.benefitDescription,
					measureType,
					quota: Number(item.defaultQuota || 1),
					unit: item.defaultUnit || (measureType === 'TIME' ? '天' : '次')
				}
			},
			mapLinkedBenefit(item) {
				return {
					id: item.benefitTemplateId,
					linkId: item.id,
					name: item.memberBenefitName,
					description: item.benefitDescription,
					measureType: this.normalizeMeasureType(item.measureType),
					quota: Number(item.benefitQuota || 1),
					unit: item.benefitUnit
				}
			},
			// 新增时避免同一商品重复配置；编辑时允许保留当前商品。
			isProductConfigured(productId) {
				return this.configuredProductIds.includes(productId) && productId !== this.originalProductId
			},
			// 同一商品内一项功能权益只允许配置一次，编辑当前项时除外。
			isBenefitConfigured(benefitId) {
				return this.formData.benefits.some((item, index) => item.id === benefitId && index !== this.editingBenefitIndex)
			},
			getMeasureTypeText(measureType) {
				return measureType === 'TIME' ? '时间型' : '次数型'
			},
			handleAddBenefit() {
				this.editingBenefitIndex = -1
				this.benefitForm = createBenefitForm()
				this.benefitDialogVisible = true
				this.$nextTick(() => this.$refs.benefitForm && this.$refs.benefitForm.clearValidate())
			},
			handleEditBenefit(row, index) {
				this.editingBenefitIndex = index
				this.benefitForm = { ...row, benefitId: row.id }
				this.benefitDialogVisible = true
				this.$nextTick(() => this.$refs.benefitForm && this.$refs.benefitForm.clearValidate())
			},
			// 选择权益后自动写入说明、计量类型及对应单位，用户只需维护额度。
			handleBenefitChange(benefitId) {
				const benefit = this.benefitCatalog.find(item => item.id === benefitId)
				if (!benefit) {
					this.benefitForm = createBenefitForm()
					return
				}
				this.benefitForm = {
					...this.benefitForm,
					...benefit,
					benefitId: benefit.id,
					unit: benefit.unit || (benefit.measureType === 'TIME' ? '天' : '次')
				}
			},
			handleConfirmBenefit() {
				this.$refs.benefitForm.validate(valid => {
					if (!valid) return
					const benefit = {
						id: this.benefitForm.benefitId,
						name: this.benefitForm.name,
						description: this.benefitForm.description,
						measureType: this.benefitForm.measureType,
						quota: Number(this.benefitForm.quota),
						unit: this.benefitForm.unit
					}
					if (this.editingBenefitIndex > -1) this.formData.benefits.splice(this.editingBenefitIndex, 1, benefit)
					else this.formData.benefits.push(benefit)
					this.benefitDialogVisible = false
				})
			},
			handleDeleteBenefit(row, index) {
				this.$modal
					.confirm(`确定删除功能权益“${row.name}”吗？`)
					.then(() => {
						this.formData.benefits.splice(index, 1)
						this.$message.success('权益已删除')
					})
					.catch(() => {})
			},
			// 保存商品权益快照；用户额度只写绑定表，不修改权益模板默认额度。
			handleSave() {
				this.$refs.configForm.validate(async valid => {
					if (!valid) return
					if (!this.formData.benefits.length) {
						this.$message.warning('请至少配置一项功能权益')
						return
					}
					this.saving = true
					try {
						await saveMyGoodsBenefitConfig({
							goodsId: this.formData.productId,
							benefits: this.formData.benefits.map((item, index) => ({
								benefitTemplateId: item.id,
								measureType: item.measureType === 'TIME' ? 'PERIOD' : 'NUMBER',
								benefitQuota: Number(item.quota),
								benefitUnit: item.unit,
								sortNo: index + 1
							}))
						})
						this.$message.success(this.isEditMode ? '商品权益配置已更新' : '商品权益配置已新增')
						this.$router.replace('/provider/configuration')
					} finally {
						this.saving = false
					}
				})
			},
			handleBack() {
				this.$router.push('/provider/configuration')
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-benefit-config-page {
		min-height: 100%;
		padding: 24px;
		overflow: hidden;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #fff;
		box-sizing: border-box;
	}
	.config-page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;
		h2 {
			margin: 0 0 7px;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 0;
			color: #7d8795;
			font-size: 14px;
		}
	}
	.config-guide-card {
		display: flex;
		align-items: center;
		margin-top: 18px;
		padding: 15px 18px;
		overflow: hidden;
		border: 1px solid #d3e5f8;
		border-radius: 8px;
		background: #f3f8fe;
		.guide-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			flex: 0 0 38px;
			height: 38px;
			margin-right: 12px;
			border-radius: 50%;
			color: #347fd0;
			font-size: 19px;
			background: #fff;
		}
		strong {
			color: #39536f;
			font-size: 15px;
		}
		p {
			margin: 4px 0 0;
			color: #6f8297;
			font-size: 14px;
			line-height: 21px;
		}
	}
	.config-form-card {
		margin-top: 18px;
		padding: 20px 22px 7px;
		overflow: hidden;
		border: 1px solid #e3e8ee;
		border-radius: 8px;
		background: #fbfcfd;
	}
	.config-section-heading {
		display: flex;
		align-items: center;
		margin-bottom: 18px;
		padding-bottom: 14px;
		border-bottom: 1px solid #e6ebf1;
		.section-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 39px;
			height: 39px;
			margin-right: 11px;
			border-radius: 8px;
			color: #317bca;
			font-size: 19px;
			background: #eaf3fd;
			&.is-green {
				color: #31956c;
				background: #eaf8f1;
			}
		}
		strong {
			display: block;
			color: #344154;
			font-size: 17px;
		}
		small {
			display: block;
			margin-top: 4px;
			color: #8b95a2;
			font-size: 14px;
		}
		&.has-action .el-button {
			margin-left: auto;
		}
	}
	.full-control {
		width: 100%;
	}
	.config-main-form ::v-deep .el-form-item {
		margin-bottom: 14px;
	}
	.config-main-form ::v-deep .el-form-item__label {
		padding-bottom: 7px;
		color: #566273;
		font-size: 14px;
		font-weight: 600;
		line-height: 20px;
	}
	.benefit-list-card {
		padding-bottom: 20px;
	}
	.benefit-name {
		color: #3f4d60;
		font-size: 14px;
	}
	.measure-tag {
		display: inline-block;
		min-width: 58px;
		padding: 4px 9px;
		border-radius: 13px;
		font-size: 14px;
		&.is-count {
			color: #2877c8;
			background: #eaf3fd;
		}
		&.is-time {
			color: #298b66;
			background: #e9f7f0;
		}
	}
	.quota-text {
		color: #dd6b32;
		font-size: 17px;
	}
	::v-deep .el-table {
		color: #586474;
		font-size: 14px;
	}
	::v-deep .el-table th {
		color: #435166;
		background: #f4f7fa;
	}
	.page-actions {
		padding: 26px 0 4px;
		text-align: center;
	}
	.page-actions .el-button {
		min-width: 130px;
	}
	.dialog-actions {
		padding: 8px 0 6px;
		text-align: center;
	}
	.dialog-actions .el-button {
		min-width: 105px;
	}
</style>

<style lang="scss">
	.provider-product-select-dropdown {
		.el-select-dropdown__item {
			height: 52px;
			padding-top: 6px;
			line-height: 20px;
		}
		.product-option-main {
			overflow: hidden;
			color: #3f4d60;
			font-weight: 600;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		.product-option-code {
			overflow: hidden;
			color: #9099a7;
			font-size: 12px;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
	}
	.provider-benefit-dialog {
		.el-dialog__body {
			padding: 4px 28px 22px;
		}
		.benefit-dialog-form .el-form-item {
			margin-bottom: 16px;
		}
		.benefit-dialog-form .el-form-item__label {
			padding-bottom: 7px;
			color: #536174;
			font-size: 14px;
			font-weight: 600;
			line-height: 20px;
		}
		.full-control {
			width: 100%;
		}
	}
</style>
